/**
 * Markdown renderizado, para leer.
 *
 * Los artefactos de un caso —el relevamiento, el analisis, la guia de resolucion— son markdown
 * que escribe un agente, con titulos, listas y **tablas**. Hasta el 2026-08-14 se leian como
 * codigo fuente: un `textarea` monoespaciado de 12 px sobre fondo negro, dentro de una columna
 * de un panel. Un informe de dos paginas ahi adentro no se lee, se descifra.
 *
 * No se escribio un parser a mano a proposito: markdown tiene tablas, listas anidadas, bloques
 * de codigo y escapado, y cada uno de esos es una forma distinta de romperse o de abrir un
 * agujero de XSS. `react-markdown` no interpreta HTML crudo salvo que se le pida, que es
 * exactamente lo que queremos para texto que escribe un modelo.
 *
 * Las clases van explicitas y no por `@tailwind/typography`: es una dependencia menos y acá
 * hacen falta cinco elementos, no cuarenta.
 */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const COMPONENTES = {
  h1: ({ node, ...props }) => (
    <h1 className="text-xl font-bold text-[#09090B] mt-6 mb-2 first:mt-0 pb-1 border-b border-brand-border" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="text-lg font-semibold text-[#09090B] mt-5 mb-2 first:mt-0" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="text-base font-semibold text-zinc-800 mt-4 mb-1.5 first:mt-0" {...props} />
  ),
  h4: ({ node, ...props }) => (
    <h4 className="text-sm font-semibold text-zinc-700 mt-3 mb-1 first:mt-0" {...props} />
  ),
  p: ({ node, ...props }) => <p className="text-sm leading-relaxed text-zinc-700 my-2" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 space-y-1 text-sm text-zinc-700" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 space-y-1 text-sm text-zinc-700" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-zinc-900" {...props} />,
  a: ({ node, ...props }) => (
    // `noopener` porque el contenido lo escribe un modelo: un link puede ir a cualquier lado.
    <a className="text-brand-primary hover:underline" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-2 border-brand-border pl-3 my-3 text-sm text-zinc-600 italic" {...props} />
  ),
  hr: ({ node, ...props }) => <hr className="my-5 border-brand-border" {...props} />,

  // Las tablas son la mitad del valor de un informe y son lo que peor se lee en texto plano.
  // Scrollean solas: una tabla ancha no puede romper el ancho de la pagina.
  table: ({ node, ...props }) => (
    <div className="my-3 overflow-x-auto border border-brand-border rounded-sm">
      <table className="w-full text-xs border-collapse" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-zinc-50" {...props} />,
  th: ({ node, ...props }) => (
    <th className="text-left font-semibold text-zinc-600 px-3 py-2 border-b border-brand-border" {...props} />
  ),
  td: ({ node, ...props }) => <td className="px-3 py-2 border-b border-zinc-100 align-top text-zinc-700" {...props} />,

  // `react-markdown` v10 saco la prop `inline` que traia v9. Sin esto, **todo** el codigo se
  // renderizaba como bloque y un `PKG_REMITOS.CONFIRMAR` en medio de una frase se iba a una
  // linea propia, cortando la lectura. Un bloque cercado siempre trae `language-*`; el codigo
  // en linea no trae className.
  code: ({ node, className, children, ...props }) => (
    /language-/.test(className || '')
      ? <code className={`font-mono text-xs block ${className || ''}`} {...props}>{children}</code>
      : <code className="font-mono text-[0.85em] bg-zinc-100 text-zinc-800 px-1 py-0.5 rounded-sm" {...props}>{children}</code>
  ),
  pre: ({ node, ...props }) => (
    <pre className="bg-zinc-50 border border-brand-border rounded-sm p-3 my-3 overflow-x-auto text-zinc-800" {...props} />
  ),
};

export default function Markdown({ children, className = '' }) {
  if (!children) {
    return <p className="text-sm text-zinc-400">Sin contenido.</p>;
  }
  return (
    <div className={className} data-testid="markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTES}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
